import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Stack,
    Spinner,
    SpinnerSize,
    DefaultButton,
    Label,
    mergeStyles,
    Icon,
    Dropdown,
    IDropdownOption,
    PrimaryButton,
    ProgressIndicator,
} from "@fluentui/react";
import { FileInfo, PreprocessInfo } from "../../../api/admin/models_admin";
import { PreprocessUI } from "./upload_preprocess";
import { getPreprocessDocumentsDownloadURL, uploadFile } from "../../../api/admin/api_admin";
import { ErrorDialog } from "../components";

export type FileStatus = "waiting" | "uploading" | "uploaded" | "uploadFailed" | "preprocessing" | "preprocessFailed" | "completed"
export interface FileUploadInfo {
    file: File
    filename: string
    status: FileStatus
    message?: string
    availablePreprocess: PreprocessInfo[]
    info?: FileInfo
}

const statusConfig: Record<FileStatus, { label: string; color: string; icon: string; spinner: boolean }> = {
    waiting:          { label: "待機中",            color: "#0078d4", icon: "CircleFill",          spinner: false  },
    uploading:        { label: "アップロード中",     color: "#0078d4", icon: "Upload",        spinner: true  },
    uploaded:         { label: "アップロード完了",   color: "#107c10", icon: "CheckMark",     spinner: false },
    uploadFailed:     { label: "アップロード失敗",   color: "#d13438", icon: "ErrorBadge",    spinner: false },
    preprocessing:    { label: "整形処理中",          color: "#0078d4", icon: "Processing",    spinner: true  },
    preprocessFailed: { label: "整形処理失敗",        color: "#d13438", icon: "ErrorBadge",    spinner: false },
    completed:        { label: "完了",              color: "#107c10", icon: "SkypeCircleCheck", spinner: false },
}

const fileInfoRowStyle = mergeStyles({
    padding: '10px 12px',
    borderRadius: 4,
    backgroundColor: "#f3f2f1",
    // 数千件規模でも表示領域外の行はレイアウト/描画を省略させる。
    // 仮想化と違って行はアンマウントされないため、整形処理の選択内容や実行中の進捗が失われない。
    // contain-intrinsic-sizeのautoは一度描画した実寸を記憶するので、整形処理を開いて
    // 行の高さが変わってもスクロールバーがずれない
    contentVisibility: 'auto',
    containIntrinsicSize: 'auto 52px',
})

/**
 * 「編集を完了」で保留していたアップロードをまとめて実行するペイン。
 * 表示されたら即座にアップロードを開始し、以降の整形処理の選択・実行は従来どおり行う。
 * 整形処理までユーザーの操作が入るため、次のフェーズへ進むのはボタン押下に委ねる。
 */
export const FileInfoUI = (props: {
    containerSeq: number
    files: File[]
    // 次のフェーズへ進むボタンの文言。呼び出し側のフェーズ構成で変わる
    completeButtonText: string
    onComplete: (processedFileInfo: FileUploadInfo[]) => void
    // アップロードや整形処理の実行中かどうかを呼び出し側へ伝える(離脱警告や閉じる操作の抑止に使う)
    onBusyChange?: (busy: boolean) => void
}) => {
    const { containerSeq, files, completeButtonText, onComplete, onBusyChange } = props
    const [fileInfo, setFileInfo] = useState<FileUploadInfo[]>(() => files.map(f => ({
        file: f,
        filename: f.name,
        status: "waiting",
        availablePreprocess: []
    })))
    const [error, setError] = useState<string | null>(null);

    // 数千件規模だと状態更新のたびに何度も全走査するのが無視できないため、1回のループでまとめて判定する
    const { allDone, isUploading, uploadDoneCount } = useMemo(() => {
        let inProgressCount = 0
        let uploadingCount = 0
        // アップロードの進捗。整形処理まで進んだものもアップロード自体は終わっている
        let uploadDoneCount = 0
        for (const v of fileInfo) {
            if (v.status === "uploading" || v.status === "preprocessing") inProgressCount++
            if (v.status === "uploading") uploadingCount++
            if (!(v.status === "waiting" || v.status === "uploading")) uploadDoneCount++
        }
        return {
            allDone: inProgressCount === 0,
            isUploading: 0 < uploadingCount,
            uploadDoneCount,
        }
    }, [fileInfo])

    // アップロードに失敗した件数。進捗の下に出す
    const uploadFailedCount = fileInfo.filter(v => v.status === "uploadFailed").length

    // React.StrictMode下ではeffectが2回実行されるため、二重にアップロードしないよう番人を置く
    const startedRef = useRef(false)

    useEffect(() => {
        if (startedRef.current) return
        startedRef.current = true
        handleFileUpload()
    }, [])

    useEffect(() => {
        onBusyChange?.(!allDone)
    }, [allDone])

    // FileRowUIのmemoを効かせるため、行ごとにクロージャを作らず参照が変わらないようにする
    const handleFileStatusChange = useCallback((filename: string, status: FileStatus) => {
        setFileInfo(prev => prev.map(v =>
            v.filename === filename ? { ...v, status } : v
        ))
    }, [])

    const handleFileUpload = async () => {
        if (files.length === 0) {
            setError("ファイルを選択してください");
            return;
        }

        // uploadFileStatus初期化
        setFileInfo(prev => prev.map((v) => ({
            ...v,
            status: "uploading",
        })))
        // ファイルをアップロードし、uploadFileStatusを更新する
        for (const file of files){
            const thisFilename = file.name
            let mes: undefined | string = undefined
            try{
                const result = await uploadFile({ container_seq: containerSeq, file: file })
                // アップロードが成功したので、整形処理のために待機
                // 利用可能な整形処理が無ければそのままcompletedとなる
                if(result.success && result.data) {
                    const preprocessAvailable = 0 < result.data.availablePreprocessList.length
                    setFileInfo(prev => prev.map((v) =>
                        v.filename === thisFilename
                            ? {
                                ...v,
                                status: preprocessAvailable ? "uploaded" : "completed", availablePreprocess: result.data!.availablePreprocessList,
                                info: result.data?.info
                        }: v
                    ))
                }
                else {
                    mes = result.message
                    throw new Error(mes || "アップロードに失敗")
                }
            } catch(e) {
                setFileInfo(prev => prev.map((v) =>
                    v.filename === thisFilename ? { ...v, status: "uploadFailed", message: mes } : v
                ))
            }
        }
    };

    return (
        <Stack tokens={{childrenGap: "16px"}} styles={{ root: { flex: 1, minHeight: 0 } }}>
            {/* ヘッダー */}
            <Stack horizontal verticalAlign="center" tokens={{childrenGap: "10px"}}>
                {!allDone && <Spinner />}
                <Label styles={{ root: { fontSize: 14, fontWeight: 700 } }}>
                    ファイルアップロード（{fileInfo.length}件）
                </Label>
            </Stack>

            {/* ファイル一覧 */}
            <Stack style={{ overflow: 'auto', flex: 1 }} tokens={{childrenGap: "10px"}}>
                {fileInfo.map((file) => (
                    <FileRowUI
                        key={file.filename}
                        file={file}
                        containerSeq={containerSeq}
                        onStatusChange={handleFileStatusChange}
                    />
                ))}
            </Stack>

            {/* アップロードの進捗 */}
            <ProgressIndicator
                label={isUploading ? "アップロード中..." : "アップロード完了"}
                description={
                    `${uploadDoneCount} / ${fileInfo.length}件`
                    + (!isUploading && 0 < uploadFailedCount ? `（${uploadFailedCount}件失敗）` : "")
                }
                percentComplete={0 < fileInfo.length ? uploadDoneCount / fileInfo.length : 1}
            />

            <Stack horizontalAlign="end" horizontal tokens={{childrenGap: "10px"}}>
                <PrimaryButton
                    text={completeButtonText}
                    onClick={() => onComplete(fileInfo)}
                    disabled={!allDone}
                />
            </Stack>

            <ErrorDialog
                content={error ?? ""}
                onDismiss={() => setError(null)}
                isOpen={Boolean(error)} />
        </Stack>
    )
}

const NONE_KEY = "__none__"

// 数千件並ぶため、状態が変わっていない行は再レンダリングしない。
// 親から渡すfileとonStatusChangeの参照が変わらないことが前提
const FileRowUI = memo((props: {
    file: FileUploadInfo
    containerSeq: number
    onStatusChange: (filename: string, status: FileStatus) => void
}) => {
    const { file, containerSeq, onStatusChange } = props
    const cfg = statusConfig[file.status]
    const showPreprocess = file.status === 'uploaded' && file.availablePreprocess.length > 0

    const [selectedKey, setSelectedKey] = useState<string>(NONE_KEY)
    const [preprocessed, setPreprocessed] = useState(false)

    const dropdownOptions: IDropdownOption[] = [
        { key: NONE_KEY, text: '整形処理を実施しない' },
        ...file.availablePreprocess.map(pp => ({ key: pp.name, text: pp.display_name })),
    ]

    const selectedPreprocess = file.availablePreprocess.find(pp => pp.name === selectedKey) ?? null

    return (
        <div className={fileInfoRowStyle}>
            <Stack tokens={{ childrenGap: 6 }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} style={{minHeight: "32px"}}>
                    {/* ステータスアイコン */}
                    <div style={{ width: 24, textAlign: 'center', flexShrink: 0 }}>
                        {cfg.spinner
                            ? <Spinner size={SpinnerSize.small} />
                            : <Icon iconName={cfg.icon} styles={{ root: { color: cfg.color, fontSize: 16 } }} />
                        }
                    </div>

                    {/* ファイル名 */}
                    <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                        <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                             title={file.filename}>
                            {file.filename}
                        </div>
                    </Stack.Item>

                    {/* 整形処理ファイルダウンロード */}
                    {
                        file.status === "completed" && preprocessed
                        && <DefaultButton 
                            text="整形結果を取得" 
                            iconProps={{iconName: "Download"}}
                            href={getPreprocessDocumentsDownloadURL(containerSeq, file.filename)}
                            target="_blank"
                        />
                    }

                    {/* ステータスバッジ */}
                    <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: cfg.color,
                        padding: '2px 8px',
                        borderRadius: 12,
                        backgroundColor: cfg.color + '1a',
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                    }}>
                        {`${cfg.label} ${file.message ? (": "+file.message) : ""}`}
                    </div>
                </Stack>

                {/* 整形処理選択 */}
                {showPreprocess && (
                    <Stack styles={{ root: { paddingLeft: 34 } }} tokens={{ childrenGap: 8 }}>
                        <Dropdown
                            label="整形処理オプション"
                            options={dropdownOptions}
                            selectedKey={selectedKey}
                            onChange={(_, opt) => opt && setSelectedKey(String(opt.key))}
                            styles={{ root: { minWidth: 240 } }}
                        />
                        {selectedPreprocess && (
                            <PreprocessUI
                                key={selectedPreprocess.name}
                                info={selectedPreprocess}
                                filename={file.filename}
                                containerSeq={containerSeq}
                                onStatusChange={(status) => {
                                    if (status === "preprocessing") {
                                        setPreprocessed(true)
                                    }
                                    onStatusChange(file.filename, status)
                                }}
                            />
                        )}
                    </Stack>
                )}
            </Stack>
        </div>
    )
})